<template>
  <b-modal
    :id="modalId"
    title="Timesheet conflict"
    ok-variant="danger"
    :ok-title="confirmLabel"
    cancel-title="Cancel"
    no-close-on-backdrop
    @ok="_confirm"
    @cancel="_cancel"
    @hidden="_cancel"
  >
    <p>{{ leadIn }} — logged hours already exist for:</p>
    <ul class="timesheet-conflict-list">
      <li v-for="(c, i) in visibleConflicts" :key="i">
        {{ c.worker_name }} - {{ formatConflictDate(c.date) }}
      </li>
    </ul>
    <p v-if="hiddenCount > 0" class="text-muted">+{{ hiddenCount }} more</p>
    <p class="text-muted">
      Deleting won't delete the logged hours themselves — they stay recorded,
      just no longer linked to this job.
    </p>
  </b-modal>
</template>

<script>
// Shared confirm dialog for the TIMESHEET_CONFLICT (409) response returned by
// destroy(), destroySeries(), and adjustSeries() in the API. Any caller shows
// this once it gets that response, then — if the user confirms — resends the
// original request with force + the exact conflicts shown here as
// `confirmed`. The API re-validates: a conflict not in `confirmed` (new hours
// logged since this dialog was shown) still blocks with a fresh 409, so this
// component's caller must be ready to show it again with the updated list.
import dayjs from 'dayjs'

const MAX_VISIBLE = 10

export default {
  name: 'TimesheetConflictModal',
  data() {
    return {
      // Multiple instances of this component can be mounted at once
      // (e.g. SchedulePage and the JobFormModal it renders both use it),
      // so the id must be unique per instance — otherwise BootstrapVue's
      // id-based $bvModal.show() opens every instance sharing the id.
      modalId: `timesheet-conflict-modal-${this._uid}`,
      conflicts: [],
      confirmLabel: 'Delete anyway',
      resolveFn: null,
    }
  },
  computed: {
    visibleConflicts() {
      return this.conflicts.slice(0, MAX_VISIBLE)
    },
    hiddenCount() {
      return Math.max(0, this.conflicts.length - MAX_VISIBLE)
    },
    leadIn() {
      const reasons = new Set(this.conflicts.map((c) => c.reason))
      if (reasons.size === 1 && reasons.has('shrink')) {
        return "Can't shrink the series that far"
      }
      if (reasons.size === 1 && reasons.has('day_removal')) {
        return "Can't remove that day"
      }
      if (reasons.size === 1 && reasons.has('delete')) {
        return "Can't delete this job"
      }
      if (reasons.size === 1 && reasons.has('series_delete')) {
        return "Can't delete this series"
      }
      return "Can't apply this change"
    },
  },
  methods: {
    formatConflictDate(dateStr) {
      return dayjs(dateStr).format('D MMM YYYY')
    },

    // conflicts: the conflicts[] array from a TIMESHEET_CONFLICT response.
    // confirmLabel: button text, e.g. "Delete anyway" or "Continue anyway".
    // Resolves to the confirmed {worker_id, date} pairs (truthy — pass this
    // straight through as the `confirmed` param on the forced retry) or
    // `null` if the user cancelled.
    show(conflicts, confirmLabel = 'Delete anyway') {
      this.conflicts = conflicts || []
      this.confirmLabel = confirmLabel
      this.$bvModal.show(this.modalId)
      return new Promise((resolve) => {
        this.resolveFn = resolve
      })
    },
    _confirm() {
      if (this.resolveFn) {
        this.resolveFn(
          this.conflicts.map((c) => ({ worker_id: c.worker_id, date: c.date })),
        )
      }
      this.resolveFn = null
    },
    _cancel() {
      if (this.resolveFn) this.resolveFn(null)
      this.resolveFn = null
    },
  },
}
</script>

<style scoped>
.timesheet-conflict-list {
  max-height: 220px;
  overflow-y: auto;
  padding-left: 1.25rem;
}
</style>
